import React from 'react';

import { Checkbox } from '@components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';

import { cn } from '@utils/index';

import {
  deriveCoverage,
  depPersonId,
  useSelectionStore,
} from '../store/selection';
import {
  MEDICAL_COVERED_PROMPT,
  MEDICAL_PLAN_SELECT_HEADING,
  MEDICAL_WAIVE_CERTIFICATION,
} from '../constants/copy';
import type { PlanDetailsMap } from '../api/planDetails';
import { W2_SELECT_TID } from '../constants/testids';
import { money } from '../format';
import {
  PlanDetailsAccordion,
  PlanLogo,
  PlanPremium,
  PlanStats,
} from './PlanCardParts';

const TID = W2_SELECT_TID.health;

/**
 * The MEDICAL/Health step is SINGLE-CHOICE (restructured 2026-07-16, captain mock):
 *
 *   1. WHO'S COVERED asked ONCE at the top — a checkbox per dependent (You is
 *      implicit/always covered) + the single global "I waive health coverage"
 *      radio (the ONLY place the certification appears);
 *   2. a plan MENU below where EXACTLY ONE plan is chosen (a radio per card;
 *      choosing one deselects any other). Each card shows its price for the
 *      currently chosen who's-covered tier.
 *
 * The chosen plan is Elected (coverage = the who's-covered tier); every other
 * offered medical plan is auto-Waived WITHOUT certification; the global waive
 * declines the whole category with the certification. Ancillary categories keep
 * their per-plan `PlanCard` layout — this restructure is medical-only.
 */
export const HealthStep: React.FC<{ details?: PlanDetailsMap }> = ({
  details,
}) => {
  const allOfferings = useSelectionStore((s) => s.context?.offerings);
  const dependents = useSelectionStore((s) => s.context?.dependents);
  const medicalCovered = useSelectionStore((s) => s.medicalCovered);
  const medicalChoice = useSelectionStore((s) => s.medicalChoice);
  const medicalWaived = useSelectionStore((s) => s.medicalWaived);
  const medicalCertified = useSelectionStore((s) => s.medicalCertified);
  const setMedicalDependent = useSelectionStore((s) => s.setMedicalDependent);
  const chooseMedicalPlan = useSelectionStore((s) => s.chooseMedicalPlan);
  const waiveMedicalCategory = useSelectionStore((s) => s.waiveMedicalCategory);
  const setMedicalCertified = useSelectionStore((s) => s.setMedicalCertified);

  const offerings = (allOfferings ?? []).filter(
    (o) => o.category === 'Medical'
  );
  const deps = dependents ?? [];
  const tier = deriveCoverage(medicalCovered, deps);

  const priceFor = (planId: number): number | undefined => {
    const offering = offerings.find((o) => o.planId === planId);
    if (tier === null) return undefined;
    return offering?.coverageOptions.find((o) => o.coverage === tier)?.price;
  };

  return (
    <div data-testid={TID.root} className="flex flex-col gap-6">
      {/* 1. Who's covered — asked ONCE. You is implicit; only dependents toggle. */}
      <section className="rounded-xl border border-[#ececf2] bg-white p-5 max-sm:p-4">
        <p className="text-[15px] font-semibold text-[#1a1a2e]">
          {MEDICAL_COVERED_PROMPT}
        </p>
        {deps.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {deps.map((dep, i) => {
              const personId = depPersonId(i);
              const checked = medicalCovered.includes(personId);
              return (
                <label
                  key={personId}
                  className="flex cursor-pointer items-center gap-3 text-[14px] text-[#1a1a2e]"
                >
                  <Checkbox
                    data-testid={`${TID.dep}-${i}`}
                    checked={checked}
                    onCheckedChange={(value) =>
                      setMedicalDependent(personId, value === true)
                    }
                    className="size-5"
                  />
                  {dep.firstName?.trim() || `Dependent ${i + 1}`}
                </label>
              );
            })}
          </div>
        )}

        {/* The single global waive — declines the WHOLE health category. */}
        <label
          className={cn(
            'mt-4 flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-[14px] transition',
            medicalWaived
              ? 'border-[#5a5a70] bg-white font-semibold text-[#1a1a2e]'
              : 'border-[#ececf2] bg-white text-[#5a5a70] hover:border-[#c7c7d2]'
          )}
        >
          <input
            type="radio"
            name="health-decision"
            data-testid={TID.waive}
            checked={medicalWaived}
            onChange={() => waiveMedicalCategory()}
            className="size-4 accent-primary"
          />
          I waive health coverage
        </label>

        {medicalWaived && (
          <Accordion
            type="single"
            collapsible
            defaultValue="certify"
            className="mt-3"
            data-testid={TID.certifyAccordion}
          >
            <AccordionItem value="certify" className="border-b-0">
              <AccordionTrigger className="text-[#5a5a70]">
                Health waiver certification
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="health-certify"
                    data-testid={TID.certifyCheckbox}
                    checked={medicalCertified}
                    onCheckedChange={(checked) =>
                      setMedicalCertified(checked === true)
                    }
                    className="mt-1 size-5 shrink-0"
                  />
                  <label
                    htmlFor="health-certify"
                    className="cursor-pointer text-[13px] leading-5 text-[#3a3a4e]"
                  >
                    {/* One source of truth: render the SAME constant that is
                        stamped into `certificationText` and submitted, so the
                        text shown is exactly the text recorded (fe-fb #6). */}
                    {MEDICAL_WAIVE_CERTIFICATION}
                  </label>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </section>

      {/* 2. Select a health plan — exactly one. */}
      <section>
        <h2 className="text-lg font-extrabold text-[#1a1a2e]">
          {MEDICAL_PLAN_SELECT_HEADING}
        </h2>
        <div
          className={cn(
            'mt-3 flex flex-col gap-3',
            medicalWaived && 'opacity-50'
          )}
        >
          {offerings.map((offering) => {
            const chosen = !medicalWaived && medicalChoice === offering.planId;
            const price = priceFor(offering.planId);
            const detail = details?.[offering.planId];
            const description = detail?.shortDescription?.trim();
            return (
              <div
                key={offering.planId}
                data-testid={`${W2_SELECT_TID.plans.card}-${offering.planId}`}
                className={cn(
                  'flex flex-col overflow-hidden rounded-2xl border bg-white p-3 shadow-[0px_8px_30px_rgba(15,15,15,0.05)] transition-all max-sm:p-2.5',
                  chosen
                    ? 'border-primary plan-selected-shadow'
                    : 'border-[#ece5d8] hover:border-[#c9c4b6]'
                )}
              >
                <label className="flex cursor-pointer flex-col gap-4 py-1 lg:flex-row lg:items-start lg:gap-3 md:py-2">
                  <div className="flex flex-1 flex-row items-start gap-3 pr-2">
                    <input
                      type="radio"
                      name="health-plan"
                      data-testid={`${TID.select}-${offering.planId}`}
                      checked={chosen}
                      onChange={() => chooseMedicalPlan(offering.planId)}
                      className="mt-1 size-4 shrink-0 accent-primary"
                    />
                    <PlanLogo plan={detail} category="Medical" />
                    <div>
                      <h3 className="mb-1.5 text-[16px] leading-[22px] font-bold text-neutral-900">
                        {detail?.name?.trim() || offering.shortName}
                      </h3>
                      <p className="max-w-xl line-clamp-3 text-xs leading-4 font-normal text-[#4a473f]">
                        {description || offering.category}
                      </p>
                    </div>
                  </div>

                  <PlanPremium
                    amount={price !== undefined ? money(price) : '—'}
                    testId={`${TID.price}-${offering.planId}`}
                    className="shrink-0 lg:w-[22%] lg:border-r lg:border-l lg:border-[#efe8dc] lg:px-4"
                  />

                  <PlanStats
                    plan={detail}
                    className="lg:w-[34%] lg:shrink-0 lg:px-3 max-lg:border-t max-lg:border-[#efe8dc] max-lg:pt-3"
                  />
                </label>
                <PlanDetailsAccordion plan={detail} planId={offering.planId} />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
